import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import api, { CarrierComplianceDocument, CarrierProfile } from '../lib/api'

const requiredDocs = ['W9', 'COI', 'MC_AUTHORITY', 'OPERATING_AUTHORITY']

export default function CarrierDetailPage() {
  const { id = '' } = useParams()
  const [carrier, setCarrier] = useState<CarrierProfile | null>(null)
  const [documents, setDocuments] = useState<CarrierComplianceDocument[]>([])
  const [complianceStatus, setComplianceStatus] = useState('NEEDS_REVIEW')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [legalName, setLegalName] = useState('')
  const [dotNumber, setDotNumber] = useState('')
  const [mcNumber, setMcNumber] = useState('')
  const [phone, setPhone] = useState('')

  const [newDocType, setNewDocType] = useState('W9')
  const [newDocStatus, setNewDocStatus] = useState('RECEIVED')
  const [newDocExpirationDate, setNewDocExpirationDate] = useState('')

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [carrierRes, complianceRes] = await Promise.all([
        api.carriers.get(id),
        api.carriers.compliance(id),
      ])
      const c = carrierRes.data.carrier
      setCarrier(c)
      setLegalName(c.legal_name ?? '')
      setDotNumber(c.dot_number ?? '')
      setMcNumber(c.mc_number ?? '')
      setPhone(c.phone ?? '')
      setDocuments(complianceRes.data.documents)
      setComplianceStatus(complianceRes.data.complianceStatus)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load carrier')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) void load()
  }, [id])

  const saveCarrier = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await api.carriers.update(id, { legalName, dotNumber, mcNumber, phone })
      setCarrier(res.data.carrier)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to update carrier')
    } finally {
      setSaving(false)
    }
  }

  const addDoc = async () => {
    setError(null)
    try {
      const res = await api.carriers.addComplianceDoc(id, {
        docType: newDocType,
        status: newDocStatus,
        expirationDate: newDocExpirationDate || undefined,
      })
      setDocuments((prev) => [res.data.document, ...prev])
      setComplianceStatus(res.data.complianceStatus)
      setNewDocExpirationDate('')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to add compliance document')
    }
  }

  if (loading) return <div className="max-w-5xl mx-auto p-6 text-sm text-gray-500">Loading carrier...</div>

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-4">
      <h1 className="text-2xl font-bold">Carrier Profile</h1>
      {error && <div className="border border-red-200 bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}

      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <input className="border rounded px-3 py-2 text-sm" value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="Legal name" />
        <div className="border rounded px-3 py-2 text-sm bg-gray-50">Compliance: <span className="font-semibold">{complianceStatus}</span></div>
        <input className="border rounded px-3 py-2 text-sm" value={dotNumber} onChange={(e) => setDotNumber(e.target.value)} placeholder="DOT number" />
        <input className="border rounded px-3 py-2 text-sm" value={mcNumber} onChange={(e) => setMcNumber(e.target.value)} placeholder="MC number" />
        <input className="border rounded px-3 py-2 text-sm" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" />
        <div className="md:col-span-2">
          <button onClick={saveCarrier} disabled={saving} className="bg-blue-700 text-white px-4 py-2 rounded text-sm">{saving ? 'Saving...' : 'Save Carrier'}</button>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-3">
        <h2 className="font-semibold">Compliance Documents</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select className="border rounded px-3 py-2 text-sm" value={newDocType} onChange={(e) => setNewDocType(e.target.value)}>
            {requiredDocs.map((doc) => <option key={doc} value={doc}>{doc}</option>)}
            <option value="BOND">BOND</option>
            <option value="OTHER">OTHER</option>
          </select>
          <select className="border rounded px-3 py-2 text-sm" value={newDocStatus} onChange={(e) => setNewDocStatus(e.target.value)}>
            <option value="RECEIVED">RECEIVED</option>
            <option value="EXPIRED">EXPIRED</option>
            <option value="MISSING">MISSING</option>
          </select>
          <input type="date" className="border rounded px-3 py-2 text-sm" value={newDocExpirationDate} onChange={(e) => setNewDocExpirationDate(e.target.value)} />
          <button onClick={addDoc} className="bg-blue-700 text-white px-3 py-2 rounded text-sm">Add Document</button>
        </div>

        {!documents.length ? <div className="text-sm text-gray-500">No documents yet.</div> : null}
        <div className="space-y-2">
          {documents.map((d) => (
            <div key={d.id} className="border rounded px-3 py-2 text-sm flex items-center justify-between gap-2">
              <div className="font-medium">{d.doc_type}</div>
              <div>{d.status}</div>
              <div className="text-gray-500">{d.expiration_date ?? '-'}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
